import type { AppDataConfig } from '@/types';
import * as v from 'valibot';
import { postQidGclidTracking } from '@/api/requests';
import { store } from '@/redux/store';
import { DialogSlice } from '@/redux/dialog';
import { selectAnswersDirectByKey } from '@/redux/slider';
import FormSchema from '@/utils/form-schema';
import { clickWithStop } from '@/utils/helper';
import Track from '@/utils/track';

const appData = {
  page: 'bussgeldcheck-wbs-legal',
  googleLogin: false,
  dateFormatToSend: null,
  externalIdGenerateMethod: 'sha1',
  iconPosition: 'after',
  cssVersion: 2,
  numericKeyboard: false,
  autofocusInput: false,
  dontLookForSecret: false,
  header: {
    showAlert: false,
    alertMessage:
      '<b>Aktuelle Information</b>: Trotz Corona-Krise steht Ihnen unser Bußgeldcheck wie gewohnt zur Verfügung.',
    alertCss: {
      background: '#eff3f5',
      color: '#3B424E',
    },
    info: 'Kostenlose Erstberatung',
    phone: '030 2555 858 00',
    openingTimes: '',
    enableStickyMobileHead: true,
  },
  banner: {
    title: [
      {
        condition: () => true,
        value: {
          xxxs: 'Lohnt sich ein <span style="font-weight:500;">Einspruch</span> gegen<br/>Bußgeld, Punkte oder Fahrverbot?',
          xxs: 'Lohnt sich ein <span style="font-weight:500;">Einspruch</span> gegen<br/>Bußgeld, Punkte oder Fahrverbot?',
          xs: 'Lohnt sich ein <span style="font-weight:500;">Einspruch</span> gegen<br/>Bußgeld, Punkte oder Fahrverbot?',
          sm: 'Lohnt sich ein <span style="font-weight:500;">Einspruch</span> gegen<br/>Bußgeld, Punkte oder Fahrverbot?',
          md: 'Lohnt sich ein <span style="font-weight:500;">Einspruch</span> gegen<br/>Bußgeld, Punkte oder Fahrverbot?',
          lg: 'Lohnt sich ein <span style="font-weight:500;">Einspruch</span> gegen<br/>Bußgeld, Punkte oder Fahrverbot?',
        },
      },
    ],
    subtitle: [
      {
        condition: () => true,
        value: 'Prüfen Sie jetzt kostenlos Ihre Möglichkeiten',
      },
    ],
  },
  sign: {
    withEnvelopIcon: true,
    withEmojies: false,
    titleSuccessColor: true,
    congratulation: [
      {
        condition: () => true,
        value: {
          title: 'Fast fertig',
          content: 'Um Ihnen das Ergebnis der kostenlosen Prüfung mitzuteilen, benötigen wir noch Ihre Kontaktdaten:',
        },
      },
    ],
    form: {
      schema: (keys) =>
        v.object({
          salutation: FormSchema.salutation(keys, ['Herr', 'Frau']),
          first_name: FormSchema.firstName(keys),
          last_name: FormSchema.lastName(keys),
          email: FormSchema.email(keys),
          phone: FormSchema.phone(keys),
          agb: FormSchema.agreement(keys, 'agb', { asNumberString: true }),
        }),
      layout: [
        {
          label: 'Anrede',
          cells: [
            {
              type: 'radio',
              key: 'salutation',
              choices: [
                {
                  label: 'Herr',
                  value: 'Herr',
                  isDefault: true,
                },
                {
                  label: 'Frau',
                  value: 'Frau',
                },
              ],
            },
          ],
        },
        {
          label: 'Name',
          cells: [
            {
              type: 'text',
              key: 'first_name',
              label: 'Vorname',
              placeholder: 'Vorname',
              placeholderShort: 'Vorname',
            },
            {
              type: 'text',
              key: 'last_name',
              label: 'Nachname',
              placeholder: 'Nachname',
              placeholderShort: 'Nachname',
            },
          ],
        },
        {
          label: 'E-Mail',
          cells: [
            {
              type: 'text',
              key: 'email',
              label: 'E-Mail',
              placeholder: 'z.B. max@mustermann.de',
              placeholderShort: 'z.B. max@mustermann.de',
            },
          ],
        },
        {
          label: 'Telefon',
          cells: [
            {
              type: 'text',
              key: 'phone',
              label: 'Telefon',
              placeholder: 'z.B. +49 170 000 000',
              placeholderShort: 'z.B. +49 170 000 000',
            },
          ],
        },
        {
          label: '',
          cells: [
            {
              type: 'checkbox',
              key: 'agb',
              label: () => (
                <div>
                  Ich bin mit der{' '}
                  <button
                    type='button'
                    onClick={(e) =>
                      clickWithStop(e, () =>
                        store.dispatch(DialogSlice.actions.openMediumCenterDialog('additional-terms.dialog')),
                      )
                    }
                  >
                    Datenweiterleitung
                  </button>{' '}
                  an SOS Verkehrsrecht einverstanden.
                </div>
              ),
            },
          ],
        },
      ],
      button: [
        {
          condition: () => true,
          value: 'Auswertung anfordern',
        },
      ],
    },
    certificates: {
      hints: ['Unser Service ist <b>TÜV geprüft</b>', 'Garantierter Datenschutz - 100% Kostenlos'],
      withTuv: true,
    },
  },
  article: {
    scroll: () => <>WARUM EINSPRUCH EINLEGEN?</>,
    title: () => (
      <>
        Warum lohnt sich ein <strong>Einspruch</strong>?
      </>
    ),
    blocks: [
      {
        title: () => <>Behörden machen häufig Fehler</>,
        text: () => (
          <>
            Für juristische Laien ist es kaum möglich, einzuschätzen, wie hoch die Erfolgschance bei einem Einspruch
            gegen den Bußgeldbescheid im Einzelfall ausfallen kann. Durch unsere über Jahre hinweg gesammelten
            Erfahrungen im Verkehrsrecht können wir Ihnen mit einer ersten Einschätzung zur Seite stehen. In einem
            kostenlosen Erstgespräch geben wir Ihnen Informationen darüber, welche Angriffspunkte etwa in der
            Vergangenheit schon zu Verfahrenseinstellungen führten oder unter welchen Bedingungen zum Beispiel auch die
            Abwandlung von einem Fahrverbot in ein höheres Bußgeld bereits möglich war. So erhalten Sie einen Eindruck
            davon, was im Einzelfall nach einer Ordnungswidrigkeit bei einem Einspruch gegen einen Bußgeldbescheid als
            Begründung dienen kann. Beachten Sie aber dabei, dass wir zu diesem Zeitpunkt die Aktenlage Ihres Falles
            noch nicht in ausreichendem Maße kennen und bewerten können. Eine Erfolgsgarantie für Ihren Einspruch gegen
            den Bußgeldbescheid können wir nicht abgeben.
          </>
        ),
        button: () => <>Jetzt Einspruch prüfen</>,
        image: 'article-image-1.svg',
      },
      {
        title: () => <>Einspruchsfrist beim Bußgeldbescheid: Behalten Sie die Zeit im Blick!</>,
        text: () => (
          <>
            Haben Sie einen Bußgeldbescheid erhalten, den Sie von unserem Team von SOS-Verkehrsrecht prüfen lassen
            wollen, sollten Sie nicht zu lange zögern. Für den Einspruch gegen den Bußgeldbescheid ist eine Frist von 14
            Tagen ab Zustellung gesetzt. Erheben Sie in diesem Zeitraum gegen das angedrohtes Fahrverbot, drohende
            Punkte oder ein Bußgeld keinen Einspruch, dann wird der Bescheid nach Fristablauf automatisch rechtskräftig.
            Eine Anfechtung der Sanktionen wie einem Bußgeld oder Fahrverbot ist dann in aller Regel nicht mehr möglich.
            Wenden Sie sich also zeitnah an uns, wenn Sie unsere Unterstützung im Einspruchsverfahren wünschen.
          </>
        ),
        button: () => <>Jetzt Einspruch prüfen</>,
        image: 'article-image-2.svg',
      },
      {
        title: () => <>Kosten für den Einspruch gegen den Bußgeldbescheid</>,
        text: () => (
          <p>
            Für den Einspruch gegen den Bußgeldbescheid können Ihnen weitere Kosten entstehen sowohl für die
            Beauftragung von einem Anwalt als auch die Tätigkeit eines Gerichts. Wir helfen Ihnen im kostenlosen
            Erstgespräch auch dabei, abzuwägen, ob das Kostenrisiko dem möglichen Nutzen gegenüber angemessen erscheint.
            Verfügen Sie über eine Verkehrsrechtsschutzversicherung kann diese gegebenenfalls für die Gerichtskosten und
            die Kosten für unsere Tätigkeit Deckungsschutz gewähren. Achten Sie dabei aber auch auf den vereinbarten
            Eigenanteil in Ihrer Versicherungspolice.
          </p>
        ),
        button: () => <>Jetzt Einspruch prüfen</>,
        image: 'article-image-3.svg',
      },
      {
        title: () => (
          <>
            Kontaktieren Sie uns für ein <span style={{ color: 'var(--success)' }}>kostenfreies Erstgespräch</span>
          </>
        ),
        text: ({ scrollToTop }) => (
          <>
            <p>
              <span>1.</span> Nutzen Sie unser
              <a style={{ color: 'var(--success)' }} onClick={scrollToTop}>
                Online-Formular
              </a>
              , um die wichtigsten Vorabinformationen an uns zu übermitteln (z. B. Art des Verstoßes, Stand des
              Verfahrens, Kontaktdaten für die Rückmeldung).
            </p>
            <p>
              <span>2.</span> Wir melden uns nach Eingang Ihrer Anfrage zeitnah bei Ihnen für ein Gespräch, in dem wir
              Ihnen erste Informationen zu einem möglichen Einspruch gegen den Bußgeldbescheid geben. Kosten entstehen
              Ihnen für dieses Erstgespräch keine.
            </p>
            <p>
              <span>3.</span> Nach dem kostenlosen Erstgespräch können Sie frei entscheiden, ob Sie unsere Unterstützung
              wünschen und uns ein Mandat erteilen.
            </p>
            <p>
              <span>4.</span> Wenn Sie unsere Vertretung im Einspruchsverfahren wünschen, werden wir hieraufhin alle
              notwendigen Schritte einleiten (insbesondere gegenüber der zuständigen Behörde Einspruch einlegen und
              Akteneinsicht beantragen). Über den aktuellen Stand halten wir Sie stets auf dem Laufenden. Die
              Kommunikation wird dabei vor allem per E-Mail stattfinden. Sie können bei Rückfragen aber auch stets unser
              Kontaktformular verwenden.
            </p>
          </>
        ),
        button: () => <>Jetzt Einspruch prüfen</>,
        image: 'article-image-4.svg',
      },
    ],
  },
  keywordWhiteList: [
    'Abstandversto%C3%9F',
    'Alkoholversto%C3%9F',
    'Anh%C3%B6rungsbogen',
    'Anhörungsbogen',
    'Anwalt%20f%C3%BCr%20Verkehrsrecht',
    'Blitzer%20Strafenkatalog',
    'Bu%C3%9Fgeldbescheid',
    'Bu%C3%9Fgeldcheck',
    'Bu%C3%9Fgeldkatalog',
    'Bu%C3%9Fgeldrechner',
    'F%C3%BChrerscheinentzug',
    'Fahrerlaubnis%20-%20Bu%C3%9Fgeldkatalog',
    'Fahrverbot',
    'Geblitzt',
    'Geschwindigkeits%C3%BCberschreitung',
    'Geschwindigkeits%C3%BCbertretung',
    'Gesetze%20-%20Verkehrsrecht',
    'Handyversto%C3%9F',
    'Messger%C3%A4te%20-%20Blitzer',
    'Ordnungswidrigkeit',
    'Punkte%20Flensburg%20Abfrage',
    'Radar%20-%20Geblitzt',
    'Rotlichtversto%C3%9F',
    'Tempo%C3%BCberschreitung',
    'Verkehrsrecht',
    'Verkehrsversto%C3%9F',
    'Verkehrszentralregister%20Abfrage',
    'Zu%20schnell%20gefahren%20-%20Geblitzt',
    'Abstandsverstoß',
    'Abstandverstoß',
    'Alkoholverstoß',
    'Anhörungsbogen',
    'Anhörungsbogen',
    'Anwalt für Verkehrsrecht',
    'Blitzer Strafenkatalog',
    'Bußgeldbescheid',
    'Bußgeldcheck',
    'Bußgeldkatalog',
    'Bußgeldrechner',
    'Führerscheinentzug',
    'Fahrerlaubnis - Bußgeldkatalog',
    'Fahrverbot',
    'Geblitzt',
    'Geschwindigkeitsüberschreitung',
    'Geschwindigkeitsübertretung',
    'Gesetze - Verkehrsrecht',
    'Handyverstoß',
    'Messgeräte - Blitzer',
    'Ordnungswidrigkeit',
    'Punkte Flensburg Abfrage',
    'Radar - Geblitzt',
    'Rotlichtverstoß',
    'Tempoüberschreitung',
    'Verkehrsrecht',
    'Verkehrsverstoß',
    'Verkehrszentralregister Abfrage',
    'Zu schnell gefahren - Geblitzt',
  ],
  selectedKeywordTitle: 'bussgeldcheck.wbs.legal',
  domainName: 'bussgeldcheck.wbs.legal',
  isInternationalizationOn: false,
  prefix: [
    {
      condition: () => true,
      value: 'SOS',
    },
  ],
  switchSlidesMode: 'on-next',
  questions: [
    {
      message: "<span style='font-weight:400;'>Bußgeldcheck starten:</span>",
      title: 'Was wird Ihnen vorgeworfen?',
      key: 'accusation',
      type: 'multiple',
      isRoot: true,
      tracking: {
        value: 'Geschwindigkeitsverstoß',
      },
      choices: [
        {
          value: 'Geschwindigkeitsverstoß',
          label: 'Geschwindig&shy;keitsverstoß',
          isPreselected: true,
          path: [
            'extra[geraetetyp]',
            'extra[wo-unterwegs]',
            'extra[wieviel-kmh]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'geschwindigkeit',
        },
        {
          value: 'Rotlichtverstoß',
          label: 'Rotlichtverstoß',
          path: [
            'extra[verkehrsteilnehmer]',
            'extra[beschaedigung]',
            'extra[ampel]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'rotlicht',
        },
        {
          value: 'Abstandsverstoß',
          label: 'Abstandsverstoß',
          path: [
            'extra[schnell-unterwegs]',
            'extra[abstand]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'abstand',
        },
        {
          value: 'Handyverstoß',
          label: 'Handyverstoß',
          path: [
            'extra[womit-unterwegs]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'handy',
        },
        {
          value: 'Alkohol- / Drogenverstoß',
          label: 'Alkoholverstoß',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[tatvorwurf]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'drogen',
        },
        {
          value: 'Parkverstoß',
          label: 'Park- / Halteverstoß',
          path: [
            'extra[parkverstoss]',
            'extra[tatvorwurf]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'park',
        },
        {
          value: '-no-answer-',
          label: 'Anderer Tatvorwurf',
          path: [
            'extra[weiterer-verstoß]',
            'extra[tatvorwurf]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Bitte konkretisieren Sie Ihren Tatvorwurf:',
      key: 'extra[weiterer-verstoß]',
      label: 'weiterer-verstoß',
      overwrites: 'accusation',
      type: 'multiple',
      choices: [
        {
          value: 'Lenk- und Ruhezeiten',
          label: 'Lenk- und Ruhezeiten',
          icon: 'ruhezeiten',
        },
        {
          value: 'Drogen',
          label: 'Drogen',
          icon: 'drogen-2',
        },
        {
          value: 'Unfall',
          label: 'Unfall',
          icon: 'unfall',
        },
        {
          value: 'Anderer Tatvorwurf',
          label: 'Anderer Tatvorwurf',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Womit erfolgte die Messung des Verstoßes?',
      key: 'extra[geraetetyp]',
      label: 'geratetyp',
      type: 'multiple',
      choices: [
        {
          value: 'Mobiler Blitzer',
          label: 'Mobiler Blitzer',
          path: [
            'extra[wo-unterwegs]',
            'extra[wieviel-kmh]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'mobiler-blitzer',
        },
        {
          value: 'Stationärer Blitzer',
          label: 'Stationärer Blitzer',
          path: [
            'extra[wo-unterwegs]',
            'extra[wieviel-kmh]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'laser',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Sonstiges',
          isDefault: true,
          path: [
            'extra[wo-unterwegs]',
            'extra[wieviel-kmh]',
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wo waren Sie unterwegs?',
      key: 'extra[wo-unterwegs]',
      label: 'wo-unterwegs',
      type: 'multiple',
      choices: [
        {
          value: 'Innerorts',
          label: 'Innerorts',
          icon: 'innerorts',
        },
        {
          value: 'Außerorts',
          label: 'Außerorts',
          icon: 'ausserorts',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wie viel km/h waren Sie zu schnell?',
      key: 'extra[wieviel-kmh]',
      label: 'wieviel-kmh',
      type: 'multiple',
      choices: [
        {
          value: '<16',
          label: '0 - 15 km/h',
          icon: '0-15',
          path: ['kein-check'],
        },
        {
          value: '16-20',
          label: '16 - 20 km/h',
          icon: '16-20',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
        },
        {
          value: '21-25',
          label: '21 - 25 km/h',
          icon: '21-25',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
        },
        {
          value: '26-30',
          label: '26 - 30 km/h',
          icon: '26-30',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
        },
        {
          value: '31-40',
          label: '31 - 40 km/h',
          icon: '31-40',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
        },
        {
          value: '>40',
          label: 'über 40 km/h',
          icon: 'over-40',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
          path: [
            'extra[fahrzeug-beziehung]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
        },
      ],
    },
    {
      title: 'Haben Sie andere Verkehrsteilnehmer gefährdet?',
      key: 'extra[verkehrsteilnehmer]',
      label: 'verkehrsteilnehmer',
      type: 'multiple',
      choices: [
        {
          value: 'Ja',
          label: 'Ja',
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Haben Sie etwas beschädigt?',
      key: 'extra[beschaedigung]',
      label: 'beschaedigung',
      type: 'multiple',
      choices: [
        {
          value: 'Ja',
          label: 'Ja',
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wie lange war die Ampel schon rot?',
      key: 'extra[ampel]',
      label: 'ampel',
      type: 'multiple',
      choices: [
        {
          value: 'unter 1 Sekunde',
          label: 'Unter 1 Sekunde',
          icon: 'sekunde-1',
        },
        {
          value: '1 Sekunde oder länger',
          label: '1 Sekunde oder länger',
          icon: 'sekunde-2',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wie schnell waren Sie unterwegs?',
      key: 'extra[schnell-unterwegs]',
      label: 'schnell-unterwegs',
      type: 'multiple',
      choices: [
        {
          value: 'Langsamer als 80 Km/h',
          label: 'Unter 80 Km/h',
          icon: 'schnell-1',
        },
        {
          value: 'Zwischen 81 Km/h und 100 Km/h',
          label: '81 - 100 km/h',
          icon: 'schnell-2',
        },
        {
          value: 'Zwischen 101 Km/h und 130 Km/h',
          label: '101 - 130 km/h',
          icon: 'schnell-3',
        },
        {
          value: 'Schneller als 131 Km/h',
          label: 'Über 131 Km/h',
          icon: 'schnell-4',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wie viel Abstand hatten Sie zu dem Fahrzeug vor Ihnen?',
      key: 'extra[abstand]',
      label: 'abstand',
      type: 'multiple',
      choices: [
        {
          value: '5/10 des Tachowertes',
          label: '5/10 des halben Tachowertes',
          icon: 'abstand-1',
        },
        {
          value: '4/10 des Tachowertes',
          label: '4/10 des halben Tachowertes',
          icon: 'abstand-2',
        },
        {
          value: '3/10 des Tachowertes',
          label: '3/10 des halben Tachowertes',
          icon: 'abstand-3',
        },
        {
          value: '2/10 des Tachowertes',
          label: '2/10 des halben Tachowertes',
          icon: 'abstand-4',
        },
        {
          value: '1/10 des Tachowertes',
          label: '1/10 des halben Tachowertes',
          icon: 'abstand-5',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Womit waren Sie unterwegs?',
      key: 'extra[womit-unterwegs]',
      label: 'womit-unterwegs',
      type: 'multiple',
      choices: [
        {
          value: 'Als Kraftwagenfahrer',
          label: 'PKW',
          isDefault: true,
          icon: 'kraftwagenfahrer',
        },
        {
          value: 'Als Fahrradfahrer',
          label: 'Fahrrad',
          icon: 'fahrradfahrer',
        },
        {
          value: 'Als Motorradfahrer',
          label: 'Motorrad',
          icon: 'motorradfahrer',
        },
        {
          value: 'Als LKW-Fahrer',
          label: 'LKW',
          icon: 'lkwfahrer',
        },
        {
          value: 'Als Busfahrer',
          label: 'Bus',
          icon: 'busfahrer',
        },
      ],
    },
    {
      title: 'Handelt es sich um einen Park- oder um einen Halteverstoß?',
      key: 'extra[parkverstoss]',
      label: 'parkverstoss',
      type: 'multiple',
      choices: [
        {
          value: 'Parkverstoß',
          label: 'Parkverstoß',
          path: [
            'extra[park-umstaende]',
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'sub-parken',
        },
        {
          value: 'Halteverstoß',
          label: 'Halteverstoß',
          path: [
            'extra[halte-umstaende]',
            'extra[halteort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'sub-halten',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Trifft eine der folgenden Umstände auf Ihren Verstoß?',
      key: 'extra[park-umstaende]',
      label: 'park-umstaende',
      type: 'multiple',
      choices: [
        {
          value: 'Behinderung',
          label: 'mit Behinderung',
          path: [
            'extra[rettungsfahrzeuge]',
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'behinderung',
        },
        {
          value: 'Gefährdung',
          label: 'mit Gefährdung',
          path: [
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'gefaehrdung',
        },
        {
          value: 'Sachbeschädigung',
          label: 'mit Sach&shy;beschädigung',
          path: [
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'beschaedigung',
        },
        {
          value: '> 15 Minuten',
          label: 'länger als 15 Minuten',
          path: [
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'over-15',
        },
        {
          value: '> 15 Minuten mit Behinderung',
          label: '> 15 Minuten mit Behinderung',
          path: [
            'extra[rettungsfahrzeuge]',
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'over-15-danger',
        },
        {
          value: 'Nichts',
          label: 'Nichts davon',
          isDefault: true,
          path: [
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'nein',
        },
      ],
    },
    {
      title: 'Wurden Rettungsfahrzeuge behindert?',
      key: 'extra[rettungsfahrzeuge]',
      label: 'rettungsfahrzeuge',
      type: 'multiple',
      choices: [
        {
          value: 'Ja',
          label: 'Ja',
          path: [
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          path: [
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          path: [
            'extra[parkort]',
            'extra[besitz-fuehrerschein]',
            'extra[punkte-flensburg]',
            'extra[blitzer]',
            'extra[fuehrerschein-angewiesen]',
            'extra[behoerde-bescheid]',
            'legal_insurance',
            'extra[anstellungsverhaeltnis]',
            'zip',
            'formular',
          ],
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wo haben Sie falsch geparkt?',
      key: 'extra[parkort]',
      label: 'parkort',
      type: 'multiple',
      choices: [
        {
          value: 'zweite Reihe',
          label: 'zweite Reihe',
          icon: 'zweite-reihe',
        },
        {
          value: 'Gehweg / Radweg',
          label: 'Gehweg / Radweg',
          icon: 'behinderung',
        },
        {
          value: 'Autobahn / Kraftfahrstraße',
          label: 'Autobahn / Kraftfahrstraße',
          icon: 'autobahn',
        },
        {
          value: 'Nichts',
          label: 'Nichts davon',
          isDefault: true,
          icon: 'nein',
        },
      ],
    },
    {
      title: 'Handelt es sich um Halten mit Behinderung, Gefährdung oder Sachbeschädigung?',
      key: 'extra[halte-umstaende]',
      label: 'halte-umstaende',
      type: 'multiple',
      choices: [
        {
          value: 'Ja',
          label: 'Ja',
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wo haben Sie falsch gehalten?',
      key: 'extra[halteort]',
      label: 'halteort',
      type: 'multiple',
      choices: [
        {
          value: 'zweite Reihe',
          label: 'zweite Reihe',
          icon: 'zweite-reihe',
        },
        {
          value: 'Schutzstreifen für Radverkehr',
          label: 'Schutzstreifen für Radverkehr',
          icon: 'schutzstreifen',
        },
        {
          value: 'Nichts',
          label: 'Nichts davon',
          isDefault: true,
          icon: 'nein',
        },
      ],
    },
    {
      title: 'Bitte beschreiben Sie kurz den Tatvorwurf:',
      key: 'extra[tatvorwurf]',
      label: 'tatvorwurf',
      type: 'textarea',
    },
    {
      title: 'Waren Sie mit einem Firmenfahrzeug, Ihrem privaten Fahrzeug oder einem Mietfahrzeug unterwegs?',
      key: 'extra[fahrzeug-beziehung]',
      label: 'fahrzeugbeziehung',
      type: 'multiple',
      choices: [
        {
          value: 'Firmenfahrzeug',
          label: 'Firmenfahrzeug',
          icon: 'firmenfahrzeug',
        },
        {
          value: 'Privates Fahrzeug',
          label: 'Privates Fahrzeug',
          icon: 'privatfahrzeug',
        },
        {
          value: 'Mietfahrzeug',
          label: 'Mietfahrzeug',
          icon: 'mietfahrzeug',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wie lange besitzen Sie schon Ihren Führerschein?',
      key: 'extra[besitz-fuehrerschein]',
      label: 'besitz-fuehrerschein',
      type: 'multiple',
      choices: [
        {
          value: 'Weniger als 2 Jahre',
          label: 'Unter 2 Jahre (Probezeit)',
          icon: 'fuehrerschein-1',
        },
        {
          value: 'Zwischen 2 Jahre und 10 Jahren',
          label: '2 - 10 Jahre',
          icon: 'fuehrerschein-2',
        },
        {
          value: 'Mehr als 10 Jahre',
          label: 'Über 10 Jahre',
          icon: 'fuehrerschein-3',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wie viele Punkte in Flensburg haben Sie bereits?',
      key: 'extra[punkte-flensburg]',
      label: 'punkte-flensburg',
      type: 'multiple',
      choices: [
        {
          value: 'Keine',
          label: 'Keine',
          icon: 'punkte-1',
        },
        {
          value: '1 - 3 (Vormerkung)',
          label: '1 - 3 Punkte',
          icon: 'punkte-2',
        },
        {
          value: '4 - 5 (Ermahnung)',
          label: '4 - 5 Punkte',
          icon: 'punkte-3',
        },
        {
          value: '6 - 7 (Verwarnung)',
          label: '6 - 7 Punkte',
          icon: 'punkte-4',
        },
        {
          value: '8 (Entzug)',
          label: '8 Punkte',
          icon: 'punkte-5',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Wurden Sie in den letzten 12 Monaten schon mal geblitzt?',
      key: 'extra[blitzer]',
      label: 'blitzer',
      type: 'multiple',
      choices: [
        {
          value: 'Ja',
          label: 'Ja',
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Sind Sie auf Ihren Führerschein angewiesen?',
      key: 'extra[fuehrerschein-angewiesen]',
      label: 'fuehrerschein-angewiesen',
      type: 'multiple',
      choices: [
        {
          value: 'Ja, aus beruflichen Gründen',
          label: 'Ja, aus beru\u00ADflichen Gründen',
          icon: 'ja',
        },
        {
          value: 'Ja, aus sonstigen Gründen',
          label: 'Ja, aus sonsti\u00ADgen Gründen',
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Welches Schreiben haben Sie zuletzt von der Behörde erhalten?',
      key: 'extra[behoerde-bescheid]',
      label: 'behoerde-bescheid',
      type: 'multiple',
      choices: [
        {
          value: 'Zeugenfragebogen',
          label: 'Zeugen\u00ADfragebogen',
          icon: 'zeugefragebogen',
        },
        {
          value: 'Anhörungsbogen',
          label: 'Anhörungsbogen',
          icon: 'anhoerungsbogen',
        },
        {
          value: 'Bußgeldbescheid',
          label: 'Bußgeldbescheid',
          icon: 'bussgeldbescheid',
        },
        {
          value: 'Noch kein Bescheid',
          label: 'Noch keine Post von der Behörde',
          icon: 'keinbescheid',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Sind Sie rechtsschutzversichert?',
      key: 'legal_insurance',
      type: 'multiple',
      choices: [
        {
          value: 'Ja',
          label: 'Ja',
          icon: 'ja',
        },
        {
          value: 'Nein',
          label: 'Nein',
          icon: 'nein',
        },
        {
          value: 'Unsicher',
          label: 'Keine Angabe / Unsicher',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'In was für einem Beschäftigungsverhältnis befinden Sie sich?',
      key: 'extra[anstellungsverhaeltnis]',
      type: 'multiple',
      choices: [
        {
          value: 'Selbststaendig/Freiberufler',
          label: 'Selbstständig / Freiberufler',
          icon: 'selbststaendig',
        },
        {
          value: 'Angestellt',
          label: 'Angestellt',
          icon: 'angestellt',
        },
        {
          value: 'Beamter',
          label: 'Beamter/-in',
          icon: 'beamter',
        },
        {
          value: 'Rentner/in',
          label: 'Rentner/-in',
          icon: 'rentner',
        },
        {
          value: 'Arbeitslos',
          label: 'Arbeitslos',
          icon: 'arbeitslos',
        },
        {
          value: 'Sonstiges',
          label: 'Keine Angabe / Sonstiges',
          isDefault: true,
          icon: 'unsicher',
        },
      ],
    },
    {
      title: 'Bitte geben Sie Ihre Postleitzahl ein:',
      key: 'zip',
      type: 'text',
      inputMode: 'numeric',
      placeholder: 'z.B 12345',
      finalValue: '',
      subline: '5-stellige Postleitzahl Ihres Wohnsitzes',
    },
    {
      title: '',
      key: 'formular',
      type: 'submission',
    },
    {
      title: '',
      key: 'kein-check',
      label: 'kein-check',
      type: 'paragraph',
      disableButtons: ['next'],
      defaultValue: '0',
      content:
        '<div><h2>Keine Hilfe möglich</h2><p>Leider können wir bei der spezifischen Anfrage zur Zeit nicht beraten.</p><p>Wir bedauern, Sie in der Angelegenheit nicht unterstützen zu können - stehen Ihnen jedoch gerne bei weiteren Anliegen zur Verfügung.</p></div>',
    },
  ],
  resultPage: {
    success: () => (
      <>
        <h3 className='h3'>Vielen Dank für Ihre kostenlose Anfrage beim Bußgeldcheck von SOS-Verkehrsrecht.de</h3>
        <p className='p'>Ihr Anliegen ist uns wichtig, daher werden wir Ihre Anfrage umgehend bearbeiten.</p>
        <p className='p'>
          Im Rahmen eines kostenlosen und unverbindlichen Telefonats wird Ihnen unser geschultes Team das Ergebnis des
          Bußgeldchecks mitteilen bzw. offene Fragen mit Ihnen klären.
        </p>
        <p className='p'>
          Mit freundlichen Grüßen
          <br />
          Bußgeldcheck - präsentiert von SOS-Verkehrsrecht.de
        </p>
      </>
    ),
    failure: () => (
      <>
        <h3 className='h3'>OH NEIN!! Irgendwas ist schief gelaufen!</h3>
        <p className='p'>Rufen Sie uns bitte unter der 030 2555 858 00 an, damit wir Ihnen weiterhelfen.</p>
      </>
    ),
  },
  additionalQuestions: null,
  certificateLogos: ['tls', 'tuv'],
  showPriceModal: 1,
  showPriceModalInsuranceCondition: {
    Ja: 'insured',
  },
  appendPoaToCTA: false,
  via: 'https://bussgeldcheck.wbs.legal',
  statics: {
    agb_version: 'einwilligung-20190617',
  },
  newClientPercentToShow: 0,
  useDynamicLandingPage: true,
  dynamicLandingPageVersion: 9,
  percentDynamicLandingPageOnPrioInsideOfficeHours: [0, 0, 0, 1],
  percentDynamicLandingPageOnPrioOutSideOfficeHours: [0, 0, 0, 1],
  openHours: ['00:00-00:00', '05:00-20:00', '05:00-20:00', '05:00-20:00', '05:00-20:00', '05:00-20:00', '05:00-17:30'],
  callDelayMinutes: 1,
  useAlternateSuccessPage: true,
  alternateSuccessPageVersion: 16,
  useInsoPriceRandomizer: false,
  insoPriceVariations: null,
  insoPriceDefault: '',
  useThanksPageWithCalculation: false,
  calculationPageUrlName: '',
  fidMapping: null,
  enableUploadFiles: false,
  redirectToOnSubmit: '',
  vollmachtApiUrl: '',
  // 2026-08-18: same IUSTA custom-field-24448 OVM clickout flow as bussgeldcheck (see
  // app/src/utils/ovm-iusta.tsx) — scoped to the long-read CTA only.
  useIustaOvmLink: true,
  usePrioCalculatorV3: 1,
  usePrioCalculatorV2: 0,
  footerDomainName: '',
  footerHomepageUrl: '',
  // TODO: Luca, do we need this action here and in several other places? Do we need to use it in some other places too?
  onSuccessPhpResolve: async (leadId) => {
    const accusation = selectAnswersDirectByKey('accusation')(store.getState());
    if (
      (accusation !== '' && Number.parseInt(accusation) >= 1 && Number.parseInt(accusation) < 5) ||
      Number.parseInt(accusation) === 6 ||
      accusation === 'Geschwindigkeitsverstoß' ||
      accusation === 'Alkoholverstoß' ||
      accusation === 'Alkohol- / Drogenverstoß' ||
      accusation === 'Abstandsverstoß' ||
      accusation === 'Rotlichtverstoß' ||
      accusation === 'Handyverstoß'
    ) {
      Track.DataLayer.MarketingConversion();
      await postQidGclidTracking(leadId);
    }
  },
} satisfies AppDataConfig as AppDataConfig;

export default appData;
